import { apiRequest } from "./client";

interface ApiSuccess<T> {
  status: "success";
  data: T;
}

/* ---------- Types ---------- */

export interface PlanLimits {
  name: string;
  max_projects: number | null;
  max_spaces: number | null;
  max_integrations: number | null;
  max_onscreen_calls?: number | null;
  memory_gb: number | null;
  cloud_execution: boolean;
}

export interface SubscriptionDetail {
  stripe_subscription_id: string;
  status: string;
  interval: "monthly" | "yearly";
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  trial_end: string | null;
  /** Subscription price in the currency's smallest unit; omitted by older backends. */
  amount_cents?: number | null;
  currency?: string | null;
}

export interface PlanUsage {
  projects_used: number;
  spaces_used: number;
  integrations_used: number;
  onscreen_calls_used?: number;
  onscreen_calls_reset_at?: string;
  memory_nodes_used: number;
  memory_gb_used: number;
}

export interface SubscriptionStatus {
  tier: "free" | "pro" | "max";
  plan_name: string;
  access_status: string;
  trial_started_at: string | null;
  trial_end: string | null;
  trial_eligible: boolean;
  has_access: boolean;
  has_pro_access: boolean;
  /** Preview-only Pro access for a verified internal email. */
  preview_pro_grant?: boolean;
  has_billing_customer: boolean;
  limits: PlanLimits;
  /** Absent when an older backend serves the payload (mixed-version window). */
  usage?: PlanUsage;
  subscription: SubscriptionDetail | null;
  prices: {
    pro_monthly: string;
    pro_yearly: string;
  };
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface Invoice {
  id: string;
  number: string | null;
  date: string;
  amount: string;
  amount_cents: number;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

/* ---------- API calls ---------- */

export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  const res = await apiRequest<ApiSuccess<SubscriptionStatus>>(
    "/api/v1/billing/subscription/",
    { silent: true },
  );
  return res.data;
}

export async function getPaymentMethods(): Promise<PaymentMethod[]> {
  const res = await apiRequest<ApiSuccess<PaymentMethod[]>>(
    "/api/v1/billing/payment-methods/",
    { silent: true },
  );
  return res.data;
}

export async function getInvoices(limit = 10): Promise<Invoice[]> {
  const res = await apiRequest<ApiSuccess<Invoice[]>>(
    `/api/v1/billing/invoices/?limit=${limit}`,
    { silent: true },
  );
  return res.data;
}

export async function createCheckout(priceId: string): Promise<string> {
  const res = await apiRequest<ApiSuccess<{ checkout_url: string }>>(
    "/api/v1/billing/checkout/",
    { method: "POST", body: { price_id: priceId } },
  );
  return res.data.checkout_url;
}

export async function openBillingPortal(): Promise<string> {
  const res = await apiRequest<ApiSuccess<{ portal_url: string }>>(
    "/api/v1/billing/portal/",
    { method: "POST" },
  );
  return res.data.portal_url;
}

/** `silent` for callers that carry on without the trial instead of reporting the failure. */
export async function startTrial({
  silent = false,
}: { silent?: boolean } = {}): Promise<SubscriptionStatus> {
  const res = await apiRequest<ApiSuccess<SubscriptionStatus>>(
    "/api/v1/billing/trial/",
    { method: "POST", silent },
  );
  window.dispatchEvent(new Event("billing-status-changed"));
  return res.data;
}
